import { useState, useRef, useCallback } from 'react';
import { qnaApi } from '../../../services/qnaApi';
import { formatAiAnswer } from '../../../utils/textFormat';

/**
 * 활성 채팅 세션(대화 내용, 실시간 답변 전송)을 관리하는 훅.
 */
export const useChatSession = (currentUser = {}, onMessageSuccess) => {
    const [messages, setMessages] = useState([]);
    const [isAiTyping, setIsAiTyping] = useState(false);
    const [isSessionLoading] = useState(false);
    
    // 세션 키 관리 (새 채팅 시 갱신됨)
    const sessionKeyRef = useRef('session_' + Date.now());

    const sendMessage = async (text, currentViewMode = 'personal') => {
        const userMsg = {
            id: Math.random().toString(36).slice(2, 9),
            role: 'user',
            text,
            timestamp: new Date(),
        };
        setMessages((prev) => [...prev, userMsg]);
        const startTime = Date.now();
        setIsAiTyping(true);

        try {
            const reqData = {
                user_id: currentUser.id || 'user_001',
                session_key: sessionKeyRef.current,
                question: text,
                category_hint: null,
                context: {
                    use_recent_history: true,
                    recent_limit: 5,
                    include_qna_ids: [],
                    exclude_qna_ids: []
                },
                options: {
                    answer_mode: "standard",
                    include_citations: true,
                    store_history: true,
                    client_timezone: "Asia/Seoul"
                }
            };

            const data = await qnaApi.ask(reqData);

            if (data && data.ok) {
                const formattedAnswer = formatAiAnswer(data.answer);

                const aiMsg = {
                    id: data.turn_id || 'ai-' + Date.now(),
                    role: 'ai',
                    text: formattedAnswer,
                    timestamp: new Date(),
                    meta: {
                        citations: data.citations || []
                    },
                    citations: data.citations || [],
                    session_key: sessionKeyRef.current
                };

                // 최소 1.5초 동안 애니메이션을 보여주기 위한 대기 (UX)
                const elapsedTime = Date.now() - startTime;
                if (elapsedTime < 1500) {
                    await new Promise(resolve => setTimeout(resolve, 1500 - elapsedTime));
                }

                setMessages((prev) => [...prev, aiMsg]);

                // 성공 시 부모 쪽의 fetchHistoryList 등을 호출 (목록 동기화)
                if (onMessageSuccess) onMessageSuccess(currentViewMode);
                
                return aiMsg;
            } else {
                throw new Error(data.message || "서버 응답 오류");
            }

        } catch (error) {
            const friendlyMsg = "⚠️ 요청하신 질문의 내용이 방대하여 답변을 준비하는 데 시간이 오래 걸리고 있습니다. 조금 더 간단하게 나누어 질문해 주시면 더욱 빠르고 정확한 안내를 드릴 수 있습니다.";

            const errorMsg = {
                id: 'error-' + Date.now(),
                role: 'ai',
                text: friendlyMsg,
                timestamp: new Date(),
            };

            // 에러 시에도 최소 노출 보장
            const elapsedTime = Date.now() - startTime;
            if (elapsedTime < 1500) {
                await new Promise(resolve => setTimeout(resolve, 1500 - elapsedTime));
            }

            setMessages((prev) => [...prev, errorMsg]);

            if (onMessageSuccess) onMessageSuccess(currentViewMode);

            return errorMsg;
        } finally {
            setIsAiTyping(false);
        }
    };

    const startNewChat = useCallback(() => {
        setMessages([]);
        sessionKeyRef.current = 'session_' + Date.now();
    }, []);

    return { messages, isAiTyping, isSessionLoading, sendMessage, startNewChat };
};
